import { Check, Loader2, ArrowRight, Sparkles } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";

// Subtle tactile press feedback for the primary money CTAs. Emil: buttons must
// feel responsive to press (scale ~0.97), fast ease-out. On reduced motion we
// drop the scale entirely — the existing Tailwind hover styles still apply.
const TAP_FEEDBACK = { whileHover: { scale: 1.02 }, whileTap: { scale: 0.97 } };
const PRESS_TRANSITION = { duration: 0.14, ease: [0.23, 1, 0.32, 1] };

/**
 * Shared plan card used on two surfaces. Rendering is split by an explicit
 * `variant` discriminator so each surface reproduces its original markup
 * verbatim — this is a deduplication refactor, not a redesign.
 *
 * - variant="landing": public landing page (LandingPage.jsx). Uses
 *   `period` (string), `ctaText`, `accent`, `special`. No icon/isCurrent.
 * - variant="billing": in-app billing (BillingSection.jsx). Uses `icon`,
 *   `billing` (cadence key), `highlight`, `isCurrent`. No period/ctaText/accent.
 */
function PlanCard({
  variant,
  // shared
  name,
  desc,
  features,
  amount,
  onClick,
  loading,
  badge,
  testid,
  // landing-only
  period,
  ctaText,
  accent = "violet",
  special,
  // billing-only
  icon: Icon,
  billing,
  highlight,
  isCurrent,
}) {
  if (variant === "billing") {
    const isSpecial = billing === "special";
    const period = isSpecial ? "USD · 7 días" : billing === "monthly" ? "USD /mes" : "USD /año";
    // For *_special: amount from API is the recurring rate ($5 or $9). User pays
    // $1 today (line item), then $4/$8 at day 7 (full price minus $1 coupon),
    // then `amount` USD/mo from month 2 onwards.
    const displayAmount = isSpecial ? 1 : amount;
    const dayThreeCharge = amount != null ? amount - 1 : null;
    return (
      <div className={`p-6 rounded-3xl border-2 transition relative ${highlight || badge ? "bg-gradient-to-br from-violet-500/15 to-fuchsia-500/8 border-violet-500/50" : "bg-white/[0.03] border-white/10"}`} data-testid={testid}>
        {highlight && (
          <div className="absolute -top-2.5 left-5 px-2.5 py-1 rounded-full bg-violet-500 text-white text-[10px] font-bold tracking-wider uppercase">Más vendido</div>
        )}
        {badge && !highlight && (
          <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white text-[10px] font-bold tracking-[0.15em] uppercase" data-testid={`${testid}-badge`}>{badge}</div>
        )}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500/30 to-fuchsia-500/10 flex items-center justify-center">
            <Icon className="w-5 h-5 text-violet-200"/>
          </div>
          <div>
            <div className="font-bold text-lg leading-tight">{name}</div>
            <div className="text-xs text-slate-400">{desc}</div>
          </div>
        </div>
        <div className="mt-5 flex items-baseline gap-1.5">
          <span className="text-4xl font-bold tabular-nums">${displayAmount?.toFixed(0) ?? "—"}</span>
          <span className="text-xs text-slate-500">{period}</span>
        </div>
        {isSpecial && dayThreeCharge != null && (
          <div className="mt-2 rounded-xl bg-violet-500/10 border border-violet-500/25 px-3 py-2 text-[11px] leading-relaxed text-violet-100" data-testid={`${testid}-special-info`}>
            <strong className="text-white">$1 hoy</strong> · acceso 7 días.
            Si no cancelas, día 7 se cobra <strong className="text-white">${dayThreeCharge.toFixed(0)}</strong> y luego <strong className="text-white">${amount?.toFixed(0)}/mes</strong>. Cancela cuando quieras.
          </div>
        )}
        <ul className="mt-5 space-y-2">
          {features.map((f, i) => (
            <li key={i} className="text-sm flex items-start gap-2">
              <Check className="w-3.5 h-3.5 text-violet-400 shrink-0 mt-0.5"/>
              <span>{f}</span>
            </li>
          ))}
        </ul>
        <PressButton onClick={onClick} disabled={loading || isCurrent} data-testid={`${testid}-cta`} interactive={!isCurrent}
          className={`mt-6 w-full flex items-center justify-center gap-2 py-3 rounded-2xl font-semibold text-sm transition ${isCurrent ? "bg-emerald-500/15 text-emerald-300 cursor-default" : highlight ? "bg-gradient-to-r from-violet-500 to-fuchsia-500 hover:from-violet-400 hover:to-fuchsia-400 text-white" : "bg-white/10 hover:bg-white/15 text-white"}`}>
          {loading ? <Loader2 className="w-4 h-4 animate-spin"/> : isCurrent ? <><Check className="w-4 h-4"/> Tu plan actual</> : <><Sparkles className="w-4 h-4"/> {billing === "special" ? "Aprovechar $1" : `Activar ${name}`}</>}
        </PressButton>
      </div>
    );
  }

  // variant === "landing"
  const borderClr = badge ? "border-violet-400/40 ring-1 ring-violet-400/30" : "border-white/10";
  const gradient = accent === "fuchsia"
    ? "from-fuchsia-500 to-violet-500"
    : "from-violet-500 to-fuchsia-500";
  return (
    <div className={`relative bg-white/[0.03] border ${borderClr} rounded-3xl p-7 sm:p-8 flex flex-col`} data-testid={testid}>
      {badge && (
        <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-gradient-to-r from-violet-500 to-fuchsia-500 text-white text-[10px] font-bold uppercase tracking-[0.15em] px-3 py-1 rounded-full">
          {badge}
        </div>
      )}
      <div className="text-xl font-black tracking-tight">{name}</div>
      <div className="mt-1 text-xs text-slate-400">{desc}</div>
      <div className="mt-5 flex items-baseline gap-1.5">
        <span className="text-5xl font-black tabular-nums">${special ? 1 : amount}</span>
        <span className="text-xs text-slate-500">{special ? "USD · 7 días" : `USD${period}`}</span>
      </div>
      {special && (
        <div className="mt-3 rounded-xl bg-violet-500/10 border border-violet-500/25 px-3 py-2 text-[11px] leading-relaxed text-violet-100">
          <strong className="text-white">$1 hoy</strong>. Si no cancelas, día 7 se cobra <strong className="text-white">${amount - 1}</strong> y luego <strong className="text-white">${amount}/mes</strong>.
        </div>
      )}
      <ul className="mt-6 space-y-2.5 flex-1">
        {features.map((f, i) => (
          <li key={i} className="flex items-start gap-2.5 text-sm text-slate-300">
            <Check className="w-4 h-4 text-violet-400 mt-0.5 flex-shrink-0" />
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <PressButton
        onClick={onClick}
        disabled={loading}
        data-testid={`${testid}-cta`}
        className={`mt-7 w-full bg-gradient-to-r ${gradient} hover:opacity-90 text-white font-bold py-3.5 rounded-xl text-sm shadow-[0_10px_40px_-12px_rgba(167,139,250,0.6)] transition-all flex items-center justify-center gap-2 disabled:opacity-50`}
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{ctaText} <ArrowRight className="w-4 h-4" /></>}
      </PressButton>
    </div>
  );
}

/**
 * A <button> with subtle scale hover/press feedback. Disabled buttons, current-
 * plan buttons (interactive=false) and reduced-motion users get a plain button
 * with the existing Tailwind styles — no motion props attached.
 */
function PressButton({ interactive = true, disabled, children, ...rest }) {
  const reduce = useReducedMotion();
  const animate = interactive && !disabled && !reduce;
  return (
    <motion.button
      disabled={disabled}
      {...(animate ? { ...TAP_FEEDBACK, transition: PRESS_TRANSITION } : {})}
      {...rest}
    >
      {children}
    </motion.button>
  );
}

export default PlanCard;
